const normalizeBaseUrl = (value?: string): string => (value || '').replace(/\/+$/, '');

const legacyApiHost = normalizeBaseUrl(import.meta.env.VITE_API_HOST);
const oauthHost = normalizeBaseUrl(import.meta.env.VITE_OAUTH_HOST);
const accountApiHost = normalizeBaseUrl(
    import.meta.env.VITE_ACCOUNT_API_HOST || oauthHost || legacyApiHost
);
const integrationsApiHost = normalizeBaseUrl(
    import.meta.env.VITE_INTEGRATIONS_API_HOST || oauthHost || legacyApiHost
);

const managedHosts = [
    legacyApiHost,
    oauthHost,
    accountApiHost,
    integrationsApiHost,
].filter(Boolean);

const INTEGRATIONS_PATH_PREFIXES = [
    '/api/workspaces/integrations',
    '/api/shopify',
    '/api/nango',
    '/api/connection-hub',
    '/api/aliexpress',
    '/api/orders',
    '/api/slack',
    '/api/webhooks',
    '/api/integrations/meta-ads',
];

const ACCOUNT_PATH_PREFIXES = [
    '/admin/api/value-ledger',
    '/api/value-ledger',
    '/api/auth',
    '/api/oauth',
    '/api/beta',
    '/api/plans',
    '/api/stripe',
    '/api/workspaces',
    '/api/pulse',
    '/api/campaign',
    '/api/reconciliation',
    '/api/tasks',
    '/api/upload_file',
];

const DUMMY_ORIGIN = 'https://bustly.local';

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const getAbsoluteBaseUrl = (value: string): string => {
    const parsedUrl = new URL(value);
    return `${parsedUrl.origin}${parsedUrl.pathname.slice(0, parsedUrl.pathname.length - parsedUrl.pathname.split('/').pop()!.length).replace(/\/$/, '')}`;
};

const matchesPrefix = (pathname: string, prefix: string): boolean =>
    pathname === prefix || pathname.startsWith(`${prefix}/`);

const isWorkspaceMcpConnectorPath = (pathname: string): boolean =>
    /^\/api\/workspaces\/[^/]+\/mcp-connectors(?:\/.*)?$/.test(pathname);

const getUrlParts = (value: string, baseURL?: string) => {
    const normalizedBaseUrl = normalizeBaseUrl(baseURL);
    const resolvedBase = normalizedBaseUrl || DUMMY_ORIGIN;
    const parsedUrl = new URL(value, resolvedBase);

    return {
        pathname: parsedUrl.pathname,
        search: parsedUrl.search,
        hash: parsedUrl.hash,
    };
};

const isManagedAbsoluteUrl = (value: string): boolean =>
    managedHosts.some((host) => host && value.startsWith(host));

const isIntegrationsBaseUrl = (value?: string): boolean => {
    const normalizedValue = normalizeBaseUrl(value);
    return !!normalizedValue && normalizedValue === integrationsApiHost;
};

const pickTargetBaseUrl = (
    pathname: string,
    baseURL?: string,
    useBaseUrlFallback = true
): string => {
    if (pathname === '/health') {
        return isIntegrationsBaseUrl(baseURL) ? integrationsApiHost : accountApiHost;
    }

    if (
        isWorkspaceMcpConnectorPath(pathname)
        || INTEGRATIONS_PATH_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))
    ) {
        return integrationsApiHost;
    }

    if (ACCOUNT_PATH_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))) {
        return accountApiHost;
    }

    return useBaseUrlFallback ? normalizeBaseUrl(baseURL) : '';
};

export const resolveApiRequestUrl = (value: string, options?: { baseURL?: string }): string => {
    if (!value) {
        return value;
    }

    const baseURL = options?.baseURL;
    const normalizedBaseUrl = normalizeBaseUrl(baseURL);
    const { pathname, search, hash } = getUrlParts(value, normalizedBaseUrl);

    if (isAbsoluteUrl(value) && !isManagedAbsoluteUrl(value)) {
        return value;
    }

    const resolvedBaseUrl = isAbsoluteUrl(value) ? getAbsoluteBaseUrl(value) : normalizedBaseUrl;
    const targetBaseUrl = pickTargetBaseUrl(
        pathname,
        resolvedBaseUrl,
        !isAbsoluteUrl(value)
    );

    if (!targetBaseUrl) {
        return value;
    }

    return `${targetBaseUrl}${pathname}${search}${hash}`;
};
